import React from "react";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SpaIcon from "@mui/icons-material/Spa";
import {useNavigate} from "react-router";
import {formatArticleDate, stripHtml, isSafeUrl} from "./newsArticleUtils";

// Re-export pure helpers for backwards compatibility
export {formatArticleDate, stripHtml};

export const NewsCard = ({article, featured = false}) => {
    const navigate = useNavigate();

    if (!article) return null;

    const dateStr = formatArticleDate(article.published);
    const plainText = stripHtml(article.text);
    const lineClampCount = article.media ? 3 : 5;
    const articleHref = `/nyheter/${article.id}`;

    const handleNavigate = (e) => {
        // Prevent outer card click if clicking an inner link or button
        if (e && e.defaultPrevented) return;
        navigate(articleHref);
    };

    return (
        <Card
            elevation={0}
            onClick={handleNavigate}
            sx={{
                height: "100%",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "rgba(40, 82, 56, 0.12)",
                borderRadius: 3,
                bgcolor: "background.paper",
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 10px 28px rgba(40, 82, 56, 0.08)",
                    borderColor: "primary.main",
                },
            }}
        >
            {article.media ? (
                <CardMedia
                    component="img"
                    image={article.media}
                    alt={article.title || "Nyhetsbilde"}
                    sx={{
                        height: featured ? {xs: 180, sm: 220} : 180,
                        objectFit: "cover",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        height: featured ? {xs: 140, sm: 160} : 120,
                        bgcolor: "rgba(40, 82, 56, 0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "primary.main",
                            zIndex: 1,
                        }}
                    >
                        <SpaIcon sx={{fontSize: 28, color: "primary.main"}} />
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                textTransform: "uppercase",
                                fontSize: "0.8rem",
                            }}
                        >
                            Aktuelt
                        </Typography>
                    </Box>
                    <SpaIcon
                        sx={{
                            fontSize: 80,
                            color: "primary.main",
                            opacity: 0.12,
                            position: "absolute",
                            right: 16,
                            bottom: -10,
                        }}
                    />
                </Box>
            )}

            <CardContent
                sx={{
                    p: {xs: 2.5, sm: 3},
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {dateStr && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 1.5}}>
                        <CalendarTodayIcon sx={{fontSize: 14, color: "text.secondary"}} />
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: "text.secondary",
                                textTransform: "capitalize",
                            }}
                        >
                            {dateStr}
                        </Typography>
                    </Box>
                )}

                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        fontSize: {xs: "1.1rem", sm: "1.2rem"},
                        lineHeight: 1.35,
                        mb: 1.5,
                    }}
                >
                    {article.title}
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        fontSize: "0.925rem",
                        lineHeight: 1.6,
                        mb: 2.5,
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: lineClampCount,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                    }}
                >
                    {plainText}
                </Typography>

                <Box sx={{mt: "auto", pt: 1}}>
                    {article.actionText && article.actionUrl && isSafeUrl(article.actionUrl) ? (
                        <Box
                            sx={{display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center"}}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                component="a"
                                href={article.actionUrl}
                                target={article.actionUrl.startsWith("http") ? "_blank" : undefined}
                                rel={article.actionUrl.startsWith("http") ? "noopener noreferrer" : undefined}
                                endIcon={
                                    article.actionUrl.startsWith("http") ? (
                                        <OpenInNewIcon fontSize="small" />
                                    ) : (
                                        <ArrowForwardIcon fontSize="small" />
                                    )
                                }
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    textTransform: "none",
                                    minHeight: 44,
                                    px: 2,
                                    "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                                }}
                            >
                                {article.actionText}
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(articleHref);
                                }}
                                aria-label={`Les hele artikkelen om ${article.title}`}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    minHeight: 44,
                                    px: 1.75,
                                    "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                                }}
                            >
                                Les mer
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(articleHref);
                            }}
                            aria-label={`Les mer om ${article.title}`}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                textTransform: "none",
                                minHeight: 44,
                                px: 2,
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            Les hele artikkelen
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
